import { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Stack,
  Typography,
  Tooltip,
  IconButton,
  ToggleButtonGroup,
  ToggleButton,
  SelectChangeEvent,
  type SxProps,
  type Theme,
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import COLORS from '../../../../resources/style/Themes';
import WorkerImage from '../../../../resources/images/alerts_visualizer_images/worker.png';
import GrassImage from '../../../../resources/images/alerts_visualizer_images/grass.png';
import StyledDropdown from './components/StyledDropdown';
import AlertLine from './components/AlertLine';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '@/resources/strings/StringKeys';

const ftToMeters = (ft: number): number => Math.round(ft * 0.3048 * 10) / 10;
const TICK_COUNT = 10; // always 11 marks (0 … TICK_COUNT intervals)
const MIN_FEET_MAX = 20;
const MIN_METERS_MAX = 10;
const EXCEEDS_MAX_FEET = 50;
const EXCEEDS_MAX_METERS = 15.2;

interface DistanceData {
  feet: number | undefined;
  meters: number | undefined;
  exceedsMax?: boolean;
}

interface SensitivityRecord {
  sensitivity: number;
  distances: {
    first: DistanceData;
    max: DistanceData;
  };
}

interface TypeData {
  displayName: string;
  sensitivities: Record<number, SensitivityRecord>;
}

// Both variants share the same flat data shape after the range-level was removed.
interface BaseVisualizerConfig {
  title: string;
  description: string;
  alertColor: string;
  sourceImage: string;
  firstAlertImage: string;
  maxAlertImage: string;
  dataSource: Record<string, TypeData>;
  initialType: string;
  initialSensitivity: number;
  hoveredSensitivity?: number;
  hoveredRange?: string; // retained for current-type hover; unused for voltage
  sx?: SxProps<Theme>;
}

interface VoltageVisualizerConfig extends BaseVisualizerConfig {
  variant: 'voltage';
}

interface CurrentVisualizerConfig extends BaseVisualizerConfig {
  variant: 'current';
}

type VisualizerConfig = VoltageVisualizerConfig | CurrentVisualizerConfig;

export default function AlertDistanceVisualizerBase(config: VisualizerConfig) {
  const { t } = useTranslation();

  const {
    variant,
    title,
    description,
    alertColor,
    sourceImage,
    firstAlertImage,
    maxAlertImage,
    dataSource,
    initialType,
    initialSensitivity,
    hoveredSensitivity,
    hoveredRange,
    sx,
  } = config;

  const [sensitivity, setSensitivity] = useState<number>(initialSensitivity);
  const [selectedType, setSelectedType] = useState<string>(initialType);
  const [userSelectedType, setUserSelectedType] = useState<string | undefined>(
    undefined,
  );
  const [unit, setUnit] = useState<'feet' | 'meters'>('feet');

  // All type options — keys of the flat dataSource
  const typeOptions = useMemo(() => Object.keys(dataSource), [dataSource]);

  // Sensitivity options derived from the first type entry
  const sensitivityOptions = useMemo(() => {
    const firstType = Object.values(dataSource)[0];
    if (!firstType?.sensitivities) return [];
    return Object.keys(firstType.sensitivities)
      .map((k) => Number(k))
      .filter((n) => !isNaN(n))
      .sort((a, b) => a - b);
  }, [dataSource]);

  // Distance record for the current selection
  const distancesRaw: SensitivityRecord | null = useMemo(
    () => dataSource?.[selectedType]?.sensitivities?.[sensitivity] ?? null,
    [dataSource, selectedType, sensitivity],
  );

  const distances = distancesRaw?.distances
    ? {
        first: distancesRaw.distances.first?.feet,
        firstExceedsMax: distancesRaw.distances.first?.exceedsMax ?? false,
        max: distancesRaw.distances.max?.feet,
        maxExceedsMax: distancesRaw.distances.max?.exceedsMax ?? false,
      }
    : null;

  const displayDistances = useMemo(() => {
    if (!distances) return null;
    if (unit === 'meters') {
      return {
        first: distances.first != null ? ftToMeters(distances.first) : null,
        firstExceedsMax: distances.firstExceedsMax,
        max: distances.max != null ? ftToMeters(distances.max) : null,
        maxExceedsMax: distances.maxExceedsMax,
      };
    }
    return {
      first: distances.first ?? null,
      firstExceedsMax: distances.firstExceedsMax,
      max: distances.max ?? null,
      maxExceedsMax: distances.maxExceedsMax,
    };
  }, [distances, unit]);

  const anyExceedsMax = distances?.firstExceedsMax || distances?.maxExceedsMax;

  // Round max distance up to the nearest multiple of 10 so tick labels are
  // always whole numbers, then divide by TICK_COUNT to get the interval.
  const effectiveMax = useMemo(() => {
    if (unit === 'meters') {
      const maxMeters = displayDistances
        ? Math.max(displayDistances.first ?? 0, displayDistances.max ?? 0)
        : 0;
      return Math.max(MIN_METERS_MAX, Math.ceil(maxMeters / 10) * 10);
    }
    const maxFeet = distances
      ? Math.max(distances.first ?? 0, distances.max ?? 0)
      : 0;
    return Math.max(MIN_FEET_MAX, Math.ceil(maxFeet / 10) * 10);
  }, [unit, distances, displayDistances]);

  const tickInterval = effectiveMax / TICK_COUNT;
  const totalTicks = TICK_COUNT;

  const firstPct =
    displayDistances?.first != null
      ? (effectiveMax - displayDistances.first) / effectiveMax
      : null;
  const maxPct =
    displayDistances?.max != null
      ? (effectiveMax - displayDistances.max) / effectiveMax
      : null;

  // True when the data record exists but both distance values are undefined —
  // indicates the sensitivity level is not supported / not recommended.
  const isNotRecommended =
    distancesRaw !== null &&
    distances !== null &&
    distances.first == null &&
    distances.max == null;

  // Sync sensitivity (and optionally type) when the parent hovers a chip
  useEffect(() => {
    if (hoveredSensitivity) {
      setSensitivity(hoveredSensitivity);
    }
    // For current variant, hoveredRange carries the amperage type
    if (variant === 'current' && hoveredRange) {
      setSelectedType(userSelectedType ?? hoveredRange);
    }
  }, [variant, hoveredSensitivity, hoveredRange, userSelectedType]);

  const handleSensitivityChange = (event: SelectChangeEvent<number>) => {
    setSensitivity(Number(event.target.value));
  };

  const handleTypeChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    setSelectedType(value);
    setUserSelectedType(value);
  };

  const handleUnitChange = (
    _event: React.MouseEvent<HTMLElement>,
    newUnit: 'feet' | 'meters' | null,
  ) => {
    if (newUnit !== null) setUnit(newUnit);
  };

  // Translate "Phase-Phase" / "Phase-Neutral" in voltage display names
  const translateVoltage = (voltageString: string): string =>
    voltageString
      .replace(/Phase-Phase/g, t(StringKeys.PHASE_PHASE))
      .replace(/Phase-Neutral/g, t(StringKeys.PHASE_NEUTRAL));

  return (
    <Paper
      elevation={0}
      sx={[{ background: '#fafafa', p: 4, maxWidth: 600 }, sx ?? false]}
    >
      {/* Header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        spacing={1}
        sx={{ mb: 0 }}
      >
        <Typography
          variant="h6"
          fontWeight={500}
          color={COLORS.SAFEGUARD_BLUE}
          sx={{ textTransform: 'capitalize', letterSpacing: 0.5 }}
        >
          {title}
        </Typography>
        <Tooltip
          title={t(StringKeys.CLICK_HERE_TO_VIEW_THE_USER_MANUAL)}
          placement="top"
          arrow
          slotProps={{ popper: { sx: { zIndex: 2200 } } }}
        >
          <IconButton
            size="small"
            sx={{ p: 0.5 }}
            component="a"
            href={`https://www.safeguardequipment.com/wp-content/uploads/2025/12/Compass-Pro-User-Guide-Dec-5.pdf#page=${variant === 'voltage' ? 10 : 13}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <LinkIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
      <Box display="flex" justifyContent="center">
        <Typography
          textAlign="center"
          sx={{ fontSize: 12, fontWeight: 500, color: COLORS.GREY, mb: 2 }}
        >
          {description}
        </Typography>
      </Box>

      {/* Dropdowns — sensitivity + voltage/current type */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        justifyContent="center"
      >
        <StyledDropdown
          value={sensitivity}
          onChange={handleSensitivityChange}
          options={sensitivityOptions}
          minWidth={60}
        />

        <StyledDropdown
          value={selectedType || ''}
          onChange={handleTypeChange}
          options={typeOptions}
          minWidth={200}
          renderOption={(option) =>
            variant === 'voltage' ? translateVoltage(option) : option
          }
          emptyMessage={
            variant === 'voltage' ? 'No voltage types' : 'No current types'
          }
        />
      </Stack>

      {/* Main Visual Layout */}
      <Box
        sx={{
          mt: 6,
          position: 'relative',
          height: 300,
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Box sx={{ position: 'relative', width: 600, height: '100%' }}>
          {isNotRecommended && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 50,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontStyle: 'italic',
                  textAlign: 'center',
                  px: 2,
                }}
              >
                {t(variant === 'current' ? StringKeys.CURRENT_SENSITIVITY_NOT_RECOMMENDED : StringKeys.SENSITIVITY_NOT_RECOMMENDED)}
              </Typography>
            </Box>
          )}

          {/* Grass (Ground) */}
          <Box
            component="img"
            src={GrassImage}
            alt="Ground"
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              width: '100%',
              height: 50,
              objectFit: 'cover',
              zIndex: 1,
            }}
          />

          {/* Worker */}
          <Box
            component="img"
            src={WorkerImage}
            alt="Worker"
            sx={{
              position: 'absolute',
              bottom: 20,
              left: 0,
              width: 80,
              height: 140,
              objectFit: 'contain',
              zIndex: 2,
            }}
          />

          {/* Right-side: unit toggle + source image */}
          <Box
            sx={{
              position: 'absolute',
              right: 0,
              bottom: 50,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
              zIndex: 4,
            }}
          >
            <ToggleButtonGroup
              value={unit}
              exclusive
              onChange={handleUnitChange}
              size="small"
              sx={{
                backgroundColor: '#fff',
                '& .MuiToggleButton-root': {
                  fontSize: 8,
                  fontWeight: 500,
                  px: 1,
                  py: 0.25,
                  border: '1px solid #d0d0d0',
                  color: '#666',
                  minWidth: 28,
                  '&.Mui-selected': {
                    backgroundColor:
                      variant === 'voltage'
                        ? COLORS.SAFEGUARD_ORANGE
                        : COLORS.SAFEGUARD_BLUE,
                    color: '#fff',
                    '&:hover': {
                      backgroundColor:
                        variant === 'voltage'
                          ? COLORS.SAFEGUARD_ORANGE
                          : COLORS.SAFEGUARD_BLUE,
                    },
                  },
                  '&:hover': { backgroundColor: '#f5f5f5' },
                },
              }}
            >
              <ToggleButton value="feet">ft</ToggleButton>
              <ToggleButton value="meters">m</ToggleButton>
            </ToggleButtonGroup>

            <Box
              component="img"
              src={sourceImage}
              alt={`${variant === 'voltage' ? 'Voltage' : 'Current'} Source`}
              sx={{ width: 80, height: 80, objectFit: 'contain' }}
            />
          </Box>

          {/* Scale Area */}
          <Box
            sx={{
              position: 'absolute',
              left: 90,
              right: 90,
              bottom: 70,
              height: 220,
              zIndex: 3,
            }}
          >
            {/* Baseline */}
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: 0,
                height: 2,
                backgroundColor: '#e5e5e5',
              }}
            />

            {/* Ticks */}
            {Array.from({ length: totalTicks + 1 }, (_, i) => {
              const value = effectiveMax - i * tickInterval;
              const pct = i / totalTicks;
              return (
                <Box
                  key={`${value}-${unit}`}
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: `${pct * 100}%`,
                    width: 2,
                    height: 10,
                    backgroundColor: '#d0d0d0',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      position: 'absolute',
                      bottom: -20,
                      left: -8,
                      width: 24,
                      textAlign: 'center',
                      color: '#8a8a8a',
                    }}
                  >
                    {value}
                    {unit === 'feet' ? 'ft' : 'm'}
                  </Typography>
                </Box>
              );
            })}

            {/* First Alert Line */}
            {firstPct != null &&
              displayDistances?.first != null &&
              distances?.first != null && (
                <AlertLine
                  position={firstPct}
                  distanceFeet={
                    displayDistances.firstExceedsMax
                      ? EXCEEDS_MAX_FEET
                      : distances.first
                  }
                  distanceMeters={
                    displayDistances.firstExceedsMax
                      ? EXCEEDS_MAX_METERS
                      : ftToMeters(distances.first)
                  }
                  displayDistance={displayDistances.first}
                  unit={unit}
                  label={t(StringKeys.FIRST_ALERT)}
                  image={firstAlertImage}
                  color={alertColor}
                  alignment="left"
                  lineStyle="dashed"
                  exceedsMax={displayDistances.firstExceedsMax}
                />
              )}

            {/* Max Alert Line */}
            {maxPct != null &&
              displayDistances?.max != null &&
              distances?.max != null && (
                <AlertLine
                  position={maxPct}
                  distanceFeet={
                    displayDistances.maxExceedsMax
                      ? EXCEEDS_MAX_FEET
                      : distances.max
                  }
                  distanceMeters={
                    displayDistances.maxExceedsMax
                      ? EXCEEDS_MAX_METERS
                      : ftToMeters(distances.max)
                  }
                  displayDistance={displayDistances.max}
                  unit={unit}
                  label={t(StringKeys.MAX_ALERT)}
                  image={maxAlertImage}
                  color={alertColor}
                  alignment="right"
                  lineStyle="solid"
                  exceedsMax={displayDistances.maxExceedsMax}
                />
              )}
          </Box>
        </Box>
      </Box>
    </Paper>
  );
}
